import { useQuery } from '@tanstack/react-query';
import { Decimal } from '@mlm/shared';
import { getCreatorMe, getWallet, listPayouts } from './api';

/**
 * Should this creator's MONEY surfaces (Wallet, Payouts, the dashboard's
 * earnings tiles) be hidden?
 *
 * A HOUSE creator is the tenant's own staff: their courses sell as house
 * products, so `orders.creator_user_id` is NULL, no `creator_ledger_entries`
 * row is ever written and the wallet can only sit at zero. Showing an
 * always-empty Wallet and a Payouts page that can never be used reads as a
 * broken portal.
 *
 * It is deliberately NOT "hide whenever isHouse": an account flipped to house
 * AFTER earning keeps a real balance, and hiding the only way to withdraw it
 * would strand the money. So the surfaces disappear only once there is nothing
 * left to see — zero available and no request still in flight.
 *
 * Both extra queries are gated on `isHouse`, so a third-party creator pays no
 * request cost for this.
 */
export function useHouseMoneyHidden(): boolean {
  const meQ = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const creator = meQ.data?.creator;
  const enabled = creator?.isHouse === true && creator.status === 'approved';
  const walletQ = useQuery({ queryKey: ['creator-wallet'], queryFn: getWallet, enabled });
  const payoutsQ = useQuery({ queryKey: ['creator-payouts'], queryFn: listPayouts, enabled });
  if (creator?.isHouse !== true) return false;
  // While the gated queries are still loading, keep the nav as it is rather
  // than flashing entries in and out.
  if (!walletQ.data || !payoutsQ.data) return true;
  const zero = new Decimal(walletQ.data.available ?? '0').isZero();
  const open = payoutsQ.data.some((r) => r.status === 'requested' || r.status === 'processing');
  return zero && !open;
}
